import { z } from "zod";
import {
  applyBuiltMessage,
  assertClientMaySetKind,
  buildEditPatch,
  buildMessage,
  type BuiltMessage,
  type MutateSurface,
} from "../message-builders";
import { deriveMessageKind } from "../message-kind-derivation";
import { messageKindSchema } from "../message-payload";
import type { MessageKind } from "../message-payload";
import { isThreadable } from "../message-predicates";
import { zql } from "../schema";

// Message writes flow through the builder helpers in ../message-builders.ts:
// builders derive preview_text + per-kind structured columns (calendar_*,
// email_*, terminal_*, call_duration_seconds) from the semantic inputs
// clients supply (kind, content, payload, mentions). Clients never send
// preview_text or structured columns directly — Zod's default .strip()
// silently drops them if they leak in.
//
// SERVER_ONLY_KINDS gate: `assertClientMaySetKind` rejects spoofed
// system/call_* kinds on the client transaction branch. `tx.location`
// differentiates client (optimistic) from server (authoritative).

// Opaque JSON at the mutator schema level; the builder layer validates the
// per-kind shape via the strict Zod schemas in ../message-payload.ts.
const messagePayloadInput = z.any().optional().nullable();

// ── D2.0: schemas exported for the server-mutator extension to re-use ──
// `server/server-mutators.ts` calls `defineMutators(clientMutators, overrides)`
// where each override re-uses the same Zod schema as the client mutator.

export const messageInsertSchema = z.object({
  id: z.string(),
  channel_id: z.string(),
  user_id: z.string(),
  content: z.string(),
  kind: messageKindSchema.optional(),
  payload: messagePayloadInput,
  thread_id: z.string().optional().nullable(),
  reply_count: z.number().optional(),
  reply_users: z.array(z.string()).optional().nullable(),
  latest_reply_at: z.number().optional().nullable(),
  is_broadcast: z.boolean().optional(),
  is_edited: z.boolean().default(false),
  mentions: z.array(z.string()).optional().nullable(),
  created_at: z.number(),
  updated_at: z.number(),
});

export const messageAddReplySchema = z.object({
  parent_id: z.string(),
  reply: z.object({
    id: z.string(),
    user_id: z.string(),
    content: z.string(),
    kind: messageKindSchema.optional(),
    payload: messagePayloadInput,
    is_broadcast: z.boolean().optional(),
    is_edited: z.boolean().default(false),
    mentions: z.array(z.string()).optional().nullable(),
    created_at: z.number(),
    updated_at: z.number(),
  }),
});

// Discord-shape inline reply schema + core live in their own file
// (`./messages-add-inline-reply-core.ts`) to keep this file under the
// 400-line ceiling. Re-exported below for backwards compatibility with
// the existing import surface.
import {
  messageAddInlineReplySchema,
  messageAddInlineReplyCore,
} from "./messages-add-inline-reply-core";
export { messageAddInlineReplySchema, messageAddInlineReplyCore };

export const messageUpdateSchema = z.object({
  // `kind` is immutable after insert — intentionally NOT accepted here.
  // Zod's default .strip() drops it if a client sneaks it into args.
  //
  // `reply_count` / `reply_users` / `latest_reply_at` / `is_broadcast` are
  // server-maintained denormalized fields written ONLY by `messageAddReplyCore`
  // (which bumps the parent's counter via a direct `tx.mutate.messages.update`,
  // not via this mutator). They are intentionally NOT accepted here — an author
  // editing their own message must not be able to overwrite the "N replies"
  // badge or flip broadcast state. Zod's default .strip() drops them if a
  // client sneaks them into args (same protection as `kind`).
  id: z.string(),
  content: z.string().optional(),
  payload: messagePayloadInput,
  mentions: z.array(z.string()).optional().nullable(),
  is_edited: z.boolean().optional(),
  updated_at: z.number().optional(),
});

// Phase 7b PR D1 — attachment row shape, kept here alongside the message
// schemas so the new atomic `messages.insertWithAttachments` /
// `actions.sendMessageWithAttachments` cores can re-use it without a
// circular import back to `./messages.ts` (which imports from here).
// The standalone `attachmentsMutators.insert` mutator in `./messages.ts`
// also reuses this schema.
export const attachmentInsertSchema = z.object({
  id: z.string(),
  message_id: z.string(),
  user_id: z.string(),
  filename: z.string(),
  file_type: z.string(),
  file_size: z.number(),
  file_category: z.string(),
  storage_key: z.string(),
  // WS-A1 slice 3b: clients no longer send `storage_url`. The Postgres
  // column is nullable as of slice 2 (PR #1454); a missing field here writes
  // NULL to the row. Slice 4 will drop the column from PG + Zero schema.
  storage_url: z.string().optional().nullable(),
  thumbnail_url: z.string().optional().nullable(),
  width: z.number().optional().nullable(),
  height: z.number().optional().nullable(),
  created_at: z.number(),
});

// Phase 7b PR D1 — atomic-mutator schemas. The wrapper schemas extend the
// existing message-insert schemas with a non-empty array of attachment
// rows so the message INSERT + N attachment INSERTs run inside a single
// Zero mutator body (and therefore a single PostgreSQL transaction
// server-side). Kind is NOT auto-derived from attachments here — callers
// must pass `kind` explicitly via the embedded message schema (matching
// the existing desktop pattern in `use-message-send.ts`).
export const messageInsertWithAttachmentsSchema = z.object({
  message: messageInsertSchema,
  attachments: z.array(attachmentInsertSchema).min(1),
});

export const messageAddReplyWithAttachmentsSchema = z.object({
  parent_id: messageAddReplySchema.shape.parent_id,
  reply: messageAddReplySchema.shape.reply,
  attachments: z.array(attachmentInsertSchema).min(1),
});

// ── D2.0: shared core helpers ──
// Each core captures the body of its parent mutator. The client mutator
// (in `messagesMutators` in `./messages.ts`) and the server-mutator
// extension (in `server/server-mutators.ts`) both call the same core. The
// server extension then runs `applyMessagePostInsertEffects(...)` (for
// INSERT-side cores) or `syncMessageMentionsJunction(...)` + a TOCTOU
// re-check (for the update core) AFTER the core's domain write. No body
// duplication.

// `currentRegion` is set server-side by the PushProcessor (see
// `server/app-zero-sync-routes.ts` — `new PushProcessor(zql, {
// sub: userId, currentRegion: process.env.ANO_REGION ?? "us" })`).
// Optional so client-side mutators (which never see env) and older
// server wirings stay type-compatible. Used today by
// `actions.createWorkspaceBootstrap` for the WS-B6 region-equality
// guard; future region-aware mutators can read it the same way.
export type MutatorCtx = { sub?: string; currentRegion?: string } | undefined;

export async function messageInsertCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageInsertSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage }> {
  if (ctx?.sub && args.user_id !== ctx.sub) {
    throw new Error("Unauthorized: user_id mismatch");
  }
  if (tx.location === "server") {
    // Spaces (channels.type='space') are non-message containers per
    // migration 120 — reject message inserts targeting them BEFORE
    // the membership check. Spaces appear in channel_members
    // (members ARE space members), so the membership check would
    // otherwise pass and a member could probe their own space
    // membership via error-message timing. Type check first ensures
    // any space-targeted insert gets the same rejection regardless
    // of membership.
    const channel = await tx.run(
      zql.channels.where("id", args.channel_id).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", args.channel_id)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }

  // Phase 7b — caller-wins fallback derive. Desktop now passes kind+payload
  // explicitly via use-message-send.ts. Any future caller that omits kind
  // (server-side text-send paths, future REST endpoints) gets the rule
  // applied automatically.
  let kind = args.kind;
  let payload = args.payload ?? null;
  if (kind === undefined) {
    const derived = deriveMessageKind({
      content: args.content,
      attachments: [],
    });
    kind = derived.kind;
    if (payload == null) payload = derived.payload;
  }
  assertClientMaySetKind(kind, tx.location);

  const sender = await tx.run(zql.users.where("id", args.user_id).one());

  const built = buildMessage({
    id: args.id,
    channel_id: args.channel_id,
    user_id: args.user_id,
    content: args.content,
    kind,
    payload,
    mentions: args.mentions ?? null,
    thread_id: args.thread_id ?? null,
    reply_count: args.reply_count ?? 0,
    reply_users: args.reply_users ?? null,
    latest_reply_at: args.latest_reply_at ?? null,
    is_broadcast: args.is_broadcast ?? false,
    is_edited: args.is_edited,
    sender_display_name: sender?.display_name ?? null,
    sender_avatar_url: sender?.avatar_url ?? null,
    created_at: args.created_at,
    updated_at: args.updated_at,
  });

  await applyBuiltMessage(tx, built);
  return { built };
}

export async function messageAddReplyCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageAddReplySchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  if (ctx?.sub && args.reply.user_id !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }

  const parent = await tx.run(zql.messages.where("id", args.parent_id).one());
  if (!parent) {
    if (tx.location === "server") {
      throw new Error(`Parent message not found: ${args.parent_id}`);
    }
    return null;
  }

  // Thread mode — the only mode this mutator handles. Walks to the
  // thread's root if the parent is itself a thread reply (parent.thread_id
  // points at the root); otherwise the parent IS the root. Counter goes
  // on the root.
  //
  // Inline replies (Discord-shape — citation in the channel timeline,
  // not in the sidebar) go through the SEPARATE `addInlineReply`
  // mutator. The caller picks at the call site so the two intents are
  // explicit and never inferred from parent state.
  const threadRootId = parent.thread_id ?? parent.id;
  const counterTarget =
    threadRootId !== parent.id
      ? await tx.run(zql.messages.where("id", threadRootId).one())
      : parent;
  if (!counterTarget) {
    if (tx.location === "server") {
      throw new Error(`Counter-target message not found: ${threadRootId}`);
    }
    return null;
  }

  // Spaces (channels.type='space', post mig 120) are non-message
  // containers. Defense-in-depth — the parent wouldn't exist on a space
  // (BEFORE INSERT trigger blocks original sends), but reject early.
  //
  // Also reject thread-replying to non-threadable kinds (system /
  // call_started_* / call_ended). Mirrors the desktop/iOS UI gates.
  if (tx.location === "server") {
    const channel = await tx.run(
      zql.channels.where("id", parent.channel_id).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    if (!isThreadable(parent.kind as MessageKind)) {
      throw new Error("Unauthorized: parent kind is not threadable");
    }
    // Channel-membership gate (Pattern 6) — derive channel_id from the
    // parent message, never trust caller input. `messages.insert` enforces
    // this; the reply path MUST too, or a kicked / non-member user who
    // learns a parent message id can post into a restricted channel and
    // trigger notification fan-out as an unauthorized poster.
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", parent.channel_id)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }

  // Phase 7b — caller-wins fallback derive (mirror of messageInsertCore).
  let kind = args.reply.kind;
  let payload = args.reply.payload ?? null;
  if (kind === undefined) {
    const derived = deriveMessageKind({
      content: args.reply.content,
      attachments: [],
    });
    kind = derived.kind;
    if (payload == null) payload = derived.payload;
  }
  assertClientMaySetKind(kind, tx.location);

  const sender = await tx.run(zql.users.where("id", args.reply.user_id).one());

  const built = buildMessage({
    id: args.reply.id,
    channel_id: parent.channel_id,
    user_id: args.reply.user_id,
    content: args.reply.content,
    kind,
    payload,
    mentions: args.reply.mentions ?? null,
    thread_id: threadRootId,
    replied_to_message_id: null,
    reply_count: 0,
    reply_users: null,
    latest_reply_at: null,
    is_broadcast: args.reply.is_broadcast ?? false,
    is_edited: args.reply.is_edited,
    sender_display_name: sender?.display_name ?? null,
    sender_avatar_url: sender?.avatar_url ?? null,
    created_at: args.reply.created_at,
    updated_at: args.reply.updated_at,
  });

  await applyBuiltMessage(tx, built);

  // Bump root's reply_count / reply_users / latest_reply_at — drives
  // the "N replies" badge on the source row.
  const existingReplyUsers =
    (counterTarget.reply_users as string[] | null) ?? [];
  const nextReplyUsers = existingReplyUsers.includes(args.reply.user_id)
    ? existingReplyUsers
    : [...existingReplyUsers, args.reply.user_id].slice(-5);

  await tx.mutate.messages.update({
    id: counterTarget.id,
    reply_count: (counterTarget.reply_count ?? 0) + 1,
    reply_users: nextReplyUsers,
    latest_reply_at: args.reply.created_at,
  });

  return { built };
}

export async function messageUpdateCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageUpdateSchema>;
  ctx: MutatorCtx;
}): Promise<{ updated: boolean }> {
  // Server-only ownership check — on the client branch we trust the
  // local mutator's optimistic apply and rely on Zero's server-side
  // reconciliation to reject unauthorized edits.
  if (tx.location === "server") {
    const msg = await tx.run(zql.messages.where("id", args.id).one());
    if (!msg) return { updated: false };
    if (msg.user_id !== ctx?.sub) {
      throw new Error("Unauthorized: not message author");
    }
  }

  // If the caller touches content or payload, re-derive preview_text +
  // structured columns via the builder. Thread metadata-only updates
  // (reply_count / reply_users / latest_reply_at) skip the builder.
  const touchesContent =
    args.content !== undefined || args.payload !== undefined;

  if (touchesContent) {
    const existing = await tx.run(zql.messages.where("id", args.id).one());
    if (!existing) {
      // Client: cache miss; optimistic apply, server will re-derive.
      // Server: race-shaped (concurrent `messages.delete` between auth
      // check above and this lookup under READ COMMITTED). We attempt
      // the update (Zero's CRUD update silently no-ops on missing PK);
      // the override's post-update TOCTOU re-check skips junction sync
      // when the row vanished.
      // Phase 7c-D: strip `mentions` from args before forwarding —
      // column was dropped (mig 127); junction sync runs in the
      // server-extension override.
      const { mentions: _mentions, ...rest } = args;
      void _mentions;
      await tx.mutate.messages.update(rest);
      return { updated: true };
    }
    const patch = buildEditPatch({
      id: args.id,
      existing: {
        kind: (existing.kind ?? "text") as Parameters<
          typeof buildEditPatch
        >[0]["existing"]["kind"],
        content: existing.content,
        payload: (existing.payload as Record<string, unknown> | null) ?? null,
      },
      changes: {
        content: args.content,
        ...(args.payload !== undefined ? { payload: args.payload } : {}),
      },
      updated_at: args.updated_at ?? Date.now(),
    });
    // Cast at the Zero boundary — the builder-derived patch carries
    // strongly-typed fields that are semantically valid but don't line
    // up with Zero's generated row type (readonly + optional wraps).
    // Phase 7c-D: `mentions` JSONB column dropped (mig 127); the input
    // `args.mentions` is consumed by `syncMessageMentionsJunction` (the
    // server-extension override below) — never written to the row.
    //
    // Server-maintained thread counters (`reply_count` / `reply_users` /
    // `latest_reply_at`) and `is_broadcast` are intentionally NOT carried
    // through: they are not in `messageUpdateSchema` and are owned solely by
    // `messageAddReplyCore`. Forwarding them here would let a message author
    // overwrite their own row's reply badge / broadcast flag.
    await tx.mutate.messages.update(
      patch as unknown as Parameters<typeof tx.mutate.messages.update>[0],
    );
  } else {
    // Phase 7c-D: strip `mentions` from the args before forwarding to
    // `messages.update`; column was dropped (mig 127). Junction sync
    // happens in the server-extension override (`syncMessageMentionsJunction`).
    const { mentions: _mentions, ...rest } = args;
    void _mentions;
    await tx.mutate.messages.update(rest);
  }

  // D2.0 #9: `syncMessageMentionsJunction` (server-only side effect that
  // writes to `message_mentions`) lives in the server-mutator extension's
  // `messages.update` override (`server/server-mutators.ts`), NOT here.
  // Per `.claude/rules/mutators.md` "Where side effects belong" —
  // shared mutators do auth + domain writes only; outbox / junction sync /
  // pg_notify go in the server extension.
  //
  // The override is also responsible for the post-update TOCTOU re-check
  // that gates junction sync against a concurrent `messages.delete`
  // landing between this core's existence lookup and `tx.mutate.messages
  // .update(...)`. Zero's server CRUD update is a blind `UPDATE ... WHERE
  // pk` with no affected-row check, so a missing row silently no-ops; the
  // override re-reads before sync. Keeping the re-check there avoids
  // paying for it on hot-path metadata-only thread updates that the
  // override never syncs junctions for.
  return { updated: true };
}

// ── Phase 7b PR D1: atomic message+attachments cores ──
//
// The N+1 client pattern (`messages.insert` + N `attachments.insert`)
// can land a `kind='image'` row whose `payload.items=[ghost-id]` if any
// attachment INSERT throws after the message INSERT succeeds. `kind` is
// immutable post-INSERT, so the row is permanently wrong. The atomic
// variants below bundle every INSERT into ONE Zero mutator body — Zero's
// `ZQLDatabase.transaction` wraps the whole body in a single PostgreSQL
// transaction server-side, so any throw rolls back BOTH the message and
// every attachment.
//
// The wrapper does NOT auto-derive kind from attachments. Callers must
// pass `args.message.kind` already-correct (matching the existing
// desktop pattern in `use-message-send.ts`); `messageInsertCore` will
// still apply its caller-wins fallback derive when kind is omitted.

/**
 * Inserts each attachment row, validating per-row that:
 *   - `user_id` matches the authenticated `ctx.sub` (server-side only).
 *   - `message_id` matches `expectedMessageId` so a malicious caller
 *     can't attach files to someone else's message.
 *
 * Channel-membership is intentionally NOT re-checked here: this helper
 * is only invoked from cores that have ALREADY verified membership for
 * the message INSERT in the same transaction. The standalone
 * `attachmentsMutators.insert` mutator (used by legacy callers) keeps
 * its own membership check.
 */
export async function insertAttachmentsForMessage(
  tx: MutateSurface,
  attachments: ReadonlyArray<z.infer<typeof attachmentInsertSchema>>,
  expectedMessageId: string,
  ctx: MutatorCtx,
): Promise<void> {
  for (const att of attachments) {
    if (ctx?.sub && att.user_id !== ctx.sub) {
      throw new Error("Unauthorized: attachment user_id mismatch");
    }
    if (att.message_id !== expectedMessageId) {
      throw new Error("attachment.message_id must match message.id");
    }
    // WS-A1 slice 3b: coerce missing/null storage_url → "" so the strict
    // Zero schema (`string()`, non-optional) accepts the row. Postgres
    // column is nullable; "" stores as "" — readers go through the URL
    // resolver helpers which prefer storage_key. Slice 4 drops the column
    // and removes this coercion.
    await tx.mutate.attachments.insert({
      ...att,
      storage_url: att.storage_url ?? "",
    });
  }
}

export async function messageInsertWithAttachmentsCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageInsertWithAttachmentsSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage }> {
  const result = await messageInsertCore({ tx, args: args.message, ctx });
  await insertAttachmentsForMessage(tx, args.attachments, args.message.id, ctx);
  return result;
}

export async function messageAddReplyWithAttachmentsCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageAddReplyWithAttachmentsSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  const result = await messageAddReplyCore({
    tx,
    args: { parent_id: args.parent_id, reply: args.reply },
    ctx,
  });
  if (result === null) return null; // parent missing — no attachments to insert
  await insertAttachmentsForMessage(tx, args.attachments, args.reply.id, ctx);
  return result;
}

// Atomic inline-reply + attachments. The Discord-shape inline reply
// previously inserted its attachments in a separate fire-and-forget loop on
// the client, so a per-attachment rejection could leave a `kind='image'`
// inline message with no attachment row (a permanent broken media card).
// This wrapper runs the message INSERT + every attachment INSERT inside one
// Zero mutator body → one PostgreSQL transaction server-side, so any throw
// rolls back both. Mirrors `messageAddReplyWithAttachmentsCore`.
export const messageAddInlineReplyWithAttachmentsSchema = z.object({
  parent_id: messageAddInlineReplySchema.shape.parent_id,
  reply: messageAddInlineReplySchema.shape.reply,
  attachments: z.array(attachmentInsertSchema).min(1),
});

export async function messageAddInlineReplyWithAttachmentsCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof messageAddInlineReplyWithAttachmentsSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  const result = await messageAddInlineReplyCore({
    tx,
    args: { parent_id: args.parent_id, reply: args.reply },
    ctx,
  });
  if (result === null) return null; // parent missing — no attachments to insert
  await insertAttachmentsForMessage(tx, args.attachments, args.reply.id, ctx);
  return result;
}
